<template>
  <div class="flex-grow-1">
    <h4 class="font-weight-bold text-center pb-3">Sign In</h4>
    <b-alert variant="warning" v-if="emailNotConfirmed" show>
      Your email address has not been confirmed. An email has been sent to you from
      <u>no-reply@timmonsgis.com</u> with a link that you must use to activate your account.
      <br>
      <br>
      Please check your email and follow the instructions so you can begin using FOResT.
      <br>
      <br>
      You should receive this email confirmation in just a few minutes.
      <br>
      <br>
      If you do not see it in your inbox:
      <ul>
        <li>
          Make sure your email is not blocking messages from <u>no-reply@timmonsgis.com</u>
        </li>
        <li>
          Also check your "spam", "junk", or other folders.
        </li>
      </ul>
    </b-alert>
    <b-form class="mb-3" v-if="!emailNotConfirmed" @submit.prevent="onSubmit" novalidate>
      <b-alert
        variant="success"
        v-if="$route.query.accountActivated === 'true' && failedAttempts === 0"
        show>
        You have successfully activated your FOResT account. Please sign in below.
      </b-alert>
      <b-alert variant="danger" v-if="$route.query.accountActivated === 'false'" show>
        Your activation link may have expired. Please sign in below to resend the activation email.
      </b-alert>
      <b-alert variant="danger" v-if="signInRequired" show>
        You are no longer signed in. Please sign in below to continue working.
      </b-alert>
      <b-alert variant="danger" v-if="this.$route.query.returnUrl" show>
        You must sign in to access the page "{{this.$route.query.returnUrl}}".
      </b-alert>
      <ValidationObserver ref="observer">
        <b-form-row class="mb-2">
          <b-col>
            <ValidationProvider name="Email" rules="required" v-slot="{ errors, classes }">
              <label for="email">
                Email:
              </label>
              <b-form-input
                type="email"
                v-model="email"
                id="email"
                name="email"
                placeholder="Email"
                autocomplete="email"
                :class="classes"
                :aria-describedby="$errorsIdString(errors, 'email')"
              />
              <InputErrors :errors="errors" errorsIdPrefix="email" />
            </ValidationProvider>
          </b-col>
        </b-form-row>
        <b-form-row class="mb-2">
          <b-col>
            <ValidationProvider name="Password" rules="required" v-slot="{ errors, classes }">
              <label for="password">
                Password:
              </label>
              <b-form-input
                v-model="password"
                id="password"
                name="password"
                type="password"
                autocomplete="current-password"
                placeholder="Password"
                :class="classes"
                class="mb-2"
                :aria-describedby="$errorsIdString(errors, 'password')"
              />
              <InputErrors :errors="errors" errorsIdPrefix="password" />
            </ValidationProvider>
          </b-col>
        </b-form-row>
      </ValidationObserver>
      <p class="text-danger font-sm" v-if="failedAttempts >= 3">
        Your account may currently be locked. Please wait 15 minutes and try again later or use
        the reset password link if you have forgotten your password.
      </p>
      <p class="text-danger font-sm" v-else-if="error">{{error}}</p>
      <template v-if="!emailNotConfirmed">
        <b-button type="submit" variant="outline-primary" pill block :disabled="submitting" aria-label="Sign In">
          Sign In
        </b-button>
      </template>
    </b-form>
    <b-link v-if="!emailNotConfirmed"
      class="text-secondary d-block text-center mt-5"
      @click="$emit('toggleResetPassword')">
      Reset your password?
    </b-link>
    <b-link v-if="emailNotConfirmed"
      class="text-primary d-block text-center"
      @click="$emit('closeModal')">
      OK
    </b-link>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import FormSubmit from '@/mixins/FormSubmit';

function initialState() {
  return {
    email: '',
    password: '',
    error: '',
    failedAttempts: 0,
    emailNotConfirmed: false,
  };
}

export default {
  name: 'SignInForm',
  mixins: [FormSubmit],
  data() {
    return initialState();
  },
  computed: {
    ...mapState('User', {
      signInRequired: state => state.signInRequired,
    }),
  },
  methods: {
    async onSubmit() {
      this.submit(
        this.$refs.observer,
        'User/signIn',
        this.$data,
      );
    },
    async submitSuccess() {
      await this.$emit('submitSuccess');
      await this.$store.dispatch('User/getUserInfo');
      if (this.$route.query.returnUrl) this.$router.push(this.$route.query.returnUrl);
      else if (this.$route.name === 'home') this.$router.push({ name: 'dashboard' });
    },
    submitError(error) {
      if (error.response.status === 403 && error.response.data.error === 'EmailNotConfirmed') {
        this.emailNotConfirmed = true;
      } else {
        this.failedAttempts += 1;
        this.error = 'Your sign in information is incorrect. Please try again. If you have forgotten your password, use the reset password link below.';
      }
    },
  },
};
</script>
